import { useEffect, useState } from 'react';
import { Activity, DollarSign, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../ui/button';
import {
    MetricCard,
    calculateVariance,
} from '../../dashboard';
import {
    getCurrentMonth,
    shiftMonth,
    formatMonthLabel,
    monthToDateRange,
} from '../../../utils/dateUtils';
import { CPTConfigValue } from '../../../services/api/CPTAnalytics/CPTConfigService';
import cptActivityService, {
    MonthlyRow,
} from '../../../services/api/CPTAnalytics/CPTActivityService';

interface CPTSummarySectionProps {
    config: CPTConfigValue | null;
}

export default function CPTSummarySection({
    config,
}: CPTSummarySectionProps) {
    const currentMonth = getCurrentMonth();
    const [selectedMonth, setSelectedMonth] = useState(currentMonth);
    const [monthData, setMonthData] = useState<MonthlyRow | null>(null);
    const [loading, setLoading] = useState(false);

    const isCurrentMonth = selectedMonth === currentMonth;
    const isFutureMonth = selectedMonth > currentMonth;

    useEffect(() => {
        const fetchMonthData = async () => {
            setLoading(true);
            try {
                const { start, end } = monthToDateRange(selectedMonth);
                const rows = await cptActivityService.getMonthlyAnalytics({
                    start,
                    end,
                });
                const match = Array.isArray(rows)
                    ? rows.find((r) => r.month === selectedMonth) ?? null
                    : null;
                setMonthData(match);
            } catch {
                setMonthData(null);
            } finally {
                setLoading(false);
            }
        };

        fetchMonthData();
    }, [selectedMonth]);

    const totalRVU = monthData?.totalRVU ?? 0;
    const totalRevenue = monthData?.estimatedRevenue ?? 0;

    const rvuTarget = config?.monthlyRvuTarget ?? null;
    const revenueTarget = config?.monthlyRevenueTarget ?? null;

    const rvuVariance = calculateVariance(totalRVU, rvuTarget);
    const revenueVariance = calculateVariance(totalRevenue, revenueTarget);

    return (
        <section>
            <div className="mb-5 flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold">
                        Estimated vs Actual
                    </h2>
                    <p className="text-sm text-muted-foreground">
                        Compare your department's monthly CPT activity against configured estimates.
                    </p>
                </div>
                <div className="flex items-center gap-1">
                    <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() =>
                            setSelectedMonth(
                                shiftMonth(selectedMonth, -1)
                            )
                        }
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="min-w-[120px] text-center text-xs font-medium">
                        {loading
                            ? '...'
                            : formatMonthLabel(selectedMonth)}
                    </span>
                    <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        disabled={isCurrentMonth || isFutureMonth}
                        onClick={() =>
                            setSelectedMonth(
                                shiftMonth(selectedMonth, 1)
                            )
                        }
                    >
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                <MetricCard
                    icon={<Activity className="h-4 w-4" />}
                    title="RVU"
                    value={totalRVU}
                    target={rvuTarget}
                    variancePct={rvuVariance.variancePct}
                />
                <MetricCard
                    icon={<DollarSign className="h-4 w-4" />}
                    title="Revenue"
                    value={totalRevenue}
                    target={revenueTarget}
                    variancePct={revenueVariance.variancePct}
                    format="currency"
                />
            </div>
        </section>
    );
}
